import React, { useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const bgImageDef: SlotGroupDefinition = {
  label: 'Parallax Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Contact us background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  const bgImage = useSlotGroup('contactform.bg_image', bgImageDef);

  // Parallax refs
  const parallaxSectionRef = useRef<HTMLDivElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = parallaxSectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* ── Section 1: Pinned parallax background + centered lead form ── */}
      <div ref={parallaxSectionRef} className="cf-parallax-section">
        {/* Parallax background image */}
        <EditableSlotGroup groupPath="contactform.bg_image" definition={bgImageDef}>
          <div ref={imgWrapRef} className="cf-parallax-img-wrap">
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                loading="eager"
                className="cf-parallax-img"
              />
            ) : (
              <div className="cf-parallax-img-fallback" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Dark overlay for legibility */}
        <div className="cf-parallax-overlay" />

        {/* Centered form */}
        <div className="cf-parallax-content">
          <div className="cf-form-wrap">
            <EditableToken path={formBlockPath} as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="cf-form-empty-card">
                  <p className="cf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* ── Section 2: Contact info in a horizontal row ── */}
      <div className="cf-info-row-section">
        <div className="cf-info-row-container">
          <div className="cf-info-row-layout">
            <PropertyInfoPanel prefix={prefix} />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="contactform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.address"      as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Parallax section ── */
        .cf-parallax-section {
          position: relative;
          overflow: hidden;
          min-height: 0;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (min-width: 768px) {
          .cf-parallax-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        /* Oversized image wrapper gives the parallax translate room to move */
        .cf-parallax-img-wrap {
          position: absolute;
          inset-inline: 0;
          top: -20%;
          height: 140%;
          will-change: transform;
        }
        .cf-parallax-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .cf-parallax-img-fallback {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-text, #1f2937);
        }

        .cf-parallax-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.58), rgba(0,0,0,0.48));
          pointer-events: none;
        }

        .cf-parallax-content {
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .cf-form-wrap {
          width: 100%;
          max-width: 640px;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* ── Contact info row section ── */
        .cf-info-row-section {
          display: none;
          background: white;
          padding: 2rem 0;
          border-top: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        @media (min-width: 768px) {
          .cf-info-row-section { display: block; }
        }
        .cf-info-row-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 1024px) {
          .cf-info-row-container { padding: 0 2rem; }
        }

        /* Override info-cards to flow horizontally */
        .cf-info-row-layout .cf-info-cards {
          flex-direction: row;
          flex-wrap: wrap;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          gap: 0.75rem;
        }
        .cf-info-row-layout .cf-card {
          flex: 1 1 180px;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: var(--vibe-radius-lg, 12px);
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
          padding: 0.75rem;
        }
        .cf-info-row-layout .cf-card:last-child {
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }

        /* Tablet: tighten the row container and card padding to match shrunken content */
        @media (min-width: 768px) and (max-width: 1023px) {
          .cf-info-row-section {
            padding: 1.25rem 0;
          }
          .cf-info-row-layout .cf-info-cards {
            gap: 0.5rem;
          }
          .cf-info-row-layout .cf-card {
            flex: 1 1 140px;
            padding: 0.5rem;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .cf-parallax-img-wrap { transform: none !important; }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'contactform.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'contactform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactform.bg_image': bgImageDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
